import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getLicenseLimits, updateLicenseLimits} from 'src/openapi/admin/apis/LicenseLimitsApi';
import {useAppDispatch} from 'src/store/hooks';

import type {Limit} from 'src/openapi/admin/models/Limit';
import type {ErrorPayload, SuccessPayload} from 'src/typedef';

type Props = {
    templateLicenseId?: number,
    defaultLicenseLimits: Limit[],
};

export const useCreateLicenseLimits = ({templateLicenseId, defaultLicenseLimits}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        if (templateLicenseId) {
            return await apiDispatch(getLicenseLimits({licenseId: templateLicenseId}, {signal}));
        } else {
            return Promise.resolve({type: '', payload: {
                success: true,
                error: false,
                data: defaultLicenseLimits,
            } as SuccessPayload<Limit[]>});
        }
    }, [templateLicenseId, defaultLicenseLimits, apiDispatch]);

    const saveDispatchAction = useCallback(async (limit: Limit[], signal: AbortSignal, licenseId?: number) => {
        if (!licenseId) {
            return Promise.resolve({type: '', payload: {
                success: false,
                error: true,
                data: {message: 'License ID is required.'},
            } as ErrorPayload});
        }

        return await apiDispatch(updateLicenseLimits({licenseId, limit}, {signal}));
    }, [apiDispatch]);

    return useCommonDataHook({
        initValue: [],
        getDispatchAction,
        saveDispatchAction,
        shouldLoadData: false,
    });
};
